import { useState } from "react";
import {
  Button,
  confirm,
  DatePicker,
  Input,
  ToggleGroup,
  ToggleGroupItem,
  toast,
} from "@/components/kit";
import type { MarketEventExpenseCategory } from "@/server/platform/db/schema/event-sales";
import { formatLak } from "@/shared/lib/currency";
import { formatDateForInput, formatDateLocal } from "@/shared/lib/date-time";
import {
  useCreateEventExpense,
  useDeleteEventExpense,
  useEventExpensesQuery,
  useUpdateEventExpense,
} from "../api/queries";
import { EVENT_EXPENSE_LABELS, EVENT_EXPENSE_OPTIONS } from "../lib/labels";
import { TouchField, touchControlClass } from "./touch-fields";

type EventSaleExpensePanelProps = {
  eventId: string;
  canWrite: boolean;
};

export function EventSaleExpensePanel({
  eventId,
  canWrite,
}: EventSaleExpensePanelProps) {
  const expenses = useEventExpensesQuery(eventId);
  const create = useCreateEventExpense(eventId);
  const update = useUpdateEventExpense(eventId);
  const remove = useDeleteEventExpense(eventId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [category, setCategory] = useState<MarketEventExpenseCategory>("booth");
  const [amount, setAmount] = useState(0);
  const [spentOn, setSpentOn] = useState(() => formatDateForInput(new Date()));
  const [note, setNote] = useState("");

  function reset() {
    setEditingId(null);
    setCategory("booth");
    setAmount(0);
    setSpentOn(formatDateForInput(new Date()));
    setNote("");
  }

  async function save() {
    if (amount <= 0 || !spentOn) return;
    const payload = {
      category,
      amount,
      spentOn,
      note: note.trim() || null,
    };
    if (editingId) {
      await update.mutateAsync({ expenseId: editingId, ...payload });
      toast.success("ອັບເດດລາຍຈ່າຍແລ້ວ");
    } else {
      await create.mutateAsync(payload);
      toast.success("ບັນທຶກລາຍຈ່າຍແລ້ວ");
    }
    reset();
  }

  async function deleteExpense(expenseId: string) {
    const ok = await confirm({
      title: "ລຶບລາຍຈ່າຍນີ້?",
      actionText: "ລຶບ",
    });
    if (!ok) return;
    await remove.mutateAsync(expenseId);
    if (editingId === expenseId) reset();
    toast.success("ລຶບລາຍຈ່າຍແລ້ວ");
  }

  const rows = expenses.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      {canWrite ? (
        <div className="brand-card flex flex-col gap-3 p-4">
          <TouchField label="ໝວດ">
            <ToggleGroup
              type="single"
              variant="outline"
              spacing={2}
              value={category}
              onValueChange={(value) => {
                if (
                  value === "booth" ||
                  value === "transport" ||
                  value === "food" ||
                  value === "supplies" ||
                  value === "other"
                ) {
                  setCategory(value);
                }
              }}
              className="flex w-full flex-wrap"
            >
              {EVENT_EXPENSE_OPTIONS.map((option) => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  className="h-11 grow"
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </TouchField>
          <TouchField label="ຈຳນວນເງິນ">
            <Input
              className={touchControlClass}
              type="number"
              min={1}
              inputMode="numeric"
              value={amount}
              onChange={(event) => setAmount(Number(event.target.value))}
            />
          </TouchField>
          <TouchField label="ວັນທີ່ຈ່າຍ">
            <DatePicker
              className="h-12 w-full text-base"
              value={spentOn ? new Date(spentOn) : null}
              captionLayout="dropdown"
              fromYear={2024}
              toYear={new Date().getFullYear() + 1}
              onChange={(date) =>
                setSpentOn(date ? formatDateForInput(date) : "")
              }
            />
          </TouchField>
          <TouchField label="ໝາຍເຫດ">
            <Input
              className={touchControlClass}
              value={note}
              placeholder="ບໍ່ບັງຄັບ"
              onChange={(event) => setNote(event.target.value)}
            />
          </TouchField>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              className="h-12 flex-1 text-base"
              disabled={amount <= 0 || create.isPending || update.isPending}
              onClick={() => void save()}
            >
              {editingId ? "ບັນທຶກການແກ້" : "ເພີ່ມລາຍຈ່າຍ"}
            </Button>
            {editingId ? (
              <Button
                type="button"
                variant="outline"
                className="h-12"
                onClick={reset}
              >
                ຍົກເລີກ
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={row.id} className="brand-card flex flex-col gap-3 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="block font-medium">
                  {EVENT_EXPENSE_LABELS[row.category]}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatDateLocal(row.spentOn)}
                  {row.note ? ` · ${row.note}` : ""}
                </span>
              </div>
              <span className="shrink-0 tabular-nums">
                {formatLak(row.amount)}
              </span>
            </div>
            {canWrite ? (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11"
                  onClick={() => {
                    setEditingId(row.id);
                    setCategory(row.category);
                    setAmount(row.amount);
                    setSpentOn(row.spentOn);
                    setNote(row.note ?? "");
                  }}
                >
                  ແກ້
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11"
                  onClick={() => void deleteExpense(row.id)}
                >
                  ລຶບ
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍຈ່າຍ</p>
      ) : null}
    </div>
  );
}
